
/* eslint-disable @typescript-eslint/camelcase */
import downloadFile from "@/app/infrastructures/misc/common-library/DownloadFile";
import formatDate from "@/app/infrastructures/misc/common-library/FormatDate";
import needsAuth from "@/app/infrastructures/misc/common-library/NeedsAuth";
import {
  fileNameFromUrl,
  formatDateNormal,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { DetailClientManagement } from "@/domain/entities/ClientManagement";
import { Options, prop, Vue } from "vue-class-component";
import PopupRenewRate from "../../../global-components/popup-renew-rate.vue";
import PopupChangeRate from "../../../global-components/popup-change-rate.vue";
import { FlagsPermissionClient } from "@/feature-flags/flags-permission-client";
import ModalChangeInsurance from "../../../global-components/modal-change-insurance.vue";
import { ClientManagementController } from "@/app/ui/controllers/ClientManagementController";
import { ClientController } from "@/app/ui/controllers/ClientController";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import ModalChangePeriodPrice from "../../modal-change-period-price.vue";
import { ClientRegistrationController } from "@/app/ui/controllers/ClientRegistrationController";
import { AbortUploadRateApiRequest } from "@/data/payload/api/ClientRegistrationApiRequest";
import ClientDiscountConfigToggle from "../../client-discount-config-toggle.vue";
class Props {
  detailData = prop<DetailClientManagement>({
    default: new DetailClientManagement(),
    required: true,
    type: Object
  });
}

const CLIENT_RATE_ERROR_FILE_EXT = ".xlsx";

@Options({
  components: {
    PopupRenewRate,
    ModalChangeInsurance,
    PopupChangeRate,
    ModalChangePeriodPrice,
    ClientDiscountConfigToggle
  }
})
class RenewalCardCard extends Vue.with(Props) {
  letterCodeModal = false;
  isUpdateInsurance = false;

  form = {
    insurance: "",
    customRates: ""
  };

  error = {
    insurance: false,
    customRates: false
  };

  get showButtonChangePeriode() {
    return this.detailData.clientCurrentRate.clientRatePriceType.toLowerCase() !== "retail"
  }

  get canShowBtnAttachment(): boolean {
    return FlagsPermissionClient.permission_client_management_detail_custom_rates_view.isEnabled();
  }

  get renewalCustomRate() {
    return this.detailData.clientRenewalRate;
  }

  get periodChangeData() {
    return this.detailData.clientRatePeriodChange;
  }

  get isPeriodRenewal() {
    return this.periodChangeData.rateVersionId > 0;
  }

  get isRenewalRatePartiallyBadge() {
    return FlagsPermissionClient.partially_status_renewal_rate.isEnabled() && this.detailData.clientRenewalRate.clientStatusBulk === "partially"
  }

  get isRenewalRate() {
    return this.detailData.clientRenewalRate.clientCustomRateId;
  }

  get isLiveRate() {
    return this.detailData.clientLiveRate.clientCustomRateId;
  }

  get isUploadingLiveRate() {
    return this.detailData.clientLiveRate.clientStatusCustomRate.toLowerCase() === "uploading";
  }

  get liveRateUpdatedAt() {
    return formatDate(this.detailData.clientLiveRate?.clientStatusUpdatedAt);
  }

  get isCancelUploadLiveRate() {
    return (
      FlagsPermissionClient.abort_upload_rate_enabled.isEnabled() &&
      this.isLiveRate &&
      this.isUploadingLiveRate &&
      this.detailData.clientLiveRate?.isStuck
    );
  }

  get renewalRatePartiallyLabel() {
    return `Partially (${this.detailData?.clientRenewalRate?.totalDataProcessed || 0}/${this.detailData?.clientRenewalRate?.totalData || 0})`
  }

  get cardTitle() {
    return this.isPeriodRenewal ? this.$t("clientManagement.renewalRateCard.updatePeriodPrice").toString() : this.$t("clientManagement.renewalRateCard.updatePrice").toString();
  }

  get isEmpty() {
    if (
      this.isPeriodRenewal ||
      this.isLiveRate ||
      this.isRejectedOrCancelled ||
      this.isApproved ||
      this.isRequested ||
      this.isUploading ||
      this.isExpired
    ) {
      return false;
    }

    return !this.renewalCustomRate.clientCustomRateId;
  }

  showModalRenewRate = false;
  onShowPopupRenewRate() {
    this.showModalRenewRate = !this.showModalRenewRate;
  }

  showModalChangeRate = false;
  onShowPopupChangeRate() {
    this.showModalChangeRate = !this.showModalChangeRate;
  }



  get status() {
    if (this.isPeriodRenewal) {
      return this.periodChangeData.status || "";
    }

    return this.renewalCustomRate.clientStatusCustomRate;
  }

  get isRequested() {
    return this.status.toLowerCase() == "requested";
  }

  get isApproved() {
    return this.status.toLowerCase() == "approved";
  }

  get isRejected() {
    return this.status.toLowerCase() == "rejected";
  }

  get isCancelled() {
    return this.status.toLowerCase() == "cancelled";
  }

  get isRejectedOrCancelled() {
    return this.isRejected || this.isCancelled;
  }

  get isExpired() {
    return this.status.toLowerCase() == "expired";
  }

  get isUploading() {
    return this.status.toLowerCase() == "uploading";
  }

  get priceType() {
    return this.detailData.clientRenewalRate.clientRatePriceType;
  }

  get priceTypeRenewRates() {
    if (this.priceType.toLowerCase() === "retail") {
      return this.$t("clientManagement.renewalRateCard.priceTypeRetail").toString();
    } else if (
      this.priceType.toLowerCase() === "normal" ||
      this.priceType.toLowerCase() === "normal_custom_3lc" ||
      this.priceType.toLowerCase() === "normal_retail_district" ||
      this.priceType.toLowerCase() === "normal_custom_3lc_retail_district"
    ) {
      return this.$t("clientManagement.renewalRateCard.priceTypeNormal").toString();
    } else if (
      this.priceType.toLowerCase() === "combination" ||
      this.priceType.toLowerCase() === "combination_custom_3lc" ||
      this.priceType.toLowerCase() === "combination_retail_district" ||
      this.priceType.toLowerCase() === "combination_custom_3lc_retail_district"
    ) {
      return this.$t("clientManagement.renewalRateCard.priceTypeCombination").toString();
    } else if (
      this.priceType.toLowerCase() === "custom_client" ||
      this.priceType.toLowerCase() === "custom_client_retail_district"
    ) {
      return this.$t("clientManagement.renewalRateCard.priceTypeCustom").toString();
    } else if (this.priceType.toLowerCase().includes("legacy")) {
      return this.priceTypeLegacyRenewRates;
    } else {
      return this.$t("clientManagement.renewalRateCard.priceTypeRetail").toString();
    }
  }

  get priceTypeLegacyRenewRates() {
    if (
      this.detailData?.clientRenewalRate?.clientVersionBaseRate &&
      this.detailData?.clientRenewalRate?.clientVersionCustomRate
    ) {
      return "mix";
    }
    if (
      !this.detailData?.clientRenewalRate?.clientVersionBaseRate &&
      this.detailData?.clientRenewalRate?.clientVersionCustomRate
    ) {
      return "custom";
    }
    return "basic";
  }

  get statusRenewalCaptions() {
    if (this.isRejectedOrCancelled) {
      return this.detailData.clientRenewalRate.clientStatusUpdatedReason;
    }

    return "";
  }

  get statusRenewalUpdateAt() {
    return formatDate(this.detailData.clientRenewalRate.clientStatusUpdatedAt);
  }

  get statusRenewalUpdatedBy() {
    return this.detailData.clientRenewalRate.clientStatusUpdatedBy;
  }

  get renewalRatePeriod() {
    return {
      start: formatDate(this.detailData.clientRenewalRate.clientRateversionStartDate, "DD MMM YYYY, HH:mm"),
      end: formatDate(this.detailData.clientRenewalRate.clientRateVersionEndDate, "DD MMM YYYY, HH:mm")
    }
  }

  get formattedCurrentPeriodDate() {
    if (!this.periodChangeData.rateVersionStartDate) {
      return "-";
    }

    return `${formatDateNormal(
      this.periodChangeData.rateVersionStartDate,
      "DD MMM YYYY, HH:mm"
    )} WIB`;
  }

  get formattedProposedEndDate() {
    if (!this.periodChangeData.proposedEndDate) {
      return "-";
    }

    return `${formatDateNormal(
      this.periodChangeData.proposedEndDate,
      "DD MMM YYYY, HH:mm"
    )} WIB`;
  }

  get renewalCustomRateValue() {
    const isCustomRate = this.detailData.clientRenewalRate.clientRatePriceType
      .split("-")[0]
      .includes("custom");
    if (this.detailData.clientIsCustomRate || isCustomRate) {
      return this.detailData.clientRenewalRate.clientVersionCustomRate || "-";
    }

    return "-";
  }

  get renewalRate() {
    return this.detailData.clientRenewalRate.clientVersionBaseRate || "-";
  }

  get lcOptions() {
    return this.detailData.clientRenewalRate.clientRateVersionCustom3lc
      ? this.detailData.clientRenewalRate.clientRateVersionCustom3lc.split(",")
      : [];
  }

  get isCustomRate() {
    return (
      this.detailData.clientRenewalRate.clientRatePriceType.split("_")[0] ===
      "custom"
    );
  }

  get isCustomRateFile() {
    return this.detailData.clientRenewalRate.clientFileCustomRate || (this.detailData.clientIsCustomRate && this.detailData.clientCurrentRate.clientRatePriceType.toLowerCase() === "legacy");
  }

  get isErrorCustomRateFile() {
    return (
      this.detailData.clientRenewalRate.clientErrorFileCustomRate &&
      this.detailData.clientRenewalRate.clientErrorNameCustomRate !==
        "error_log_.csv"
    );
  }

  // download file
  download(url: string, fileName: string): void {
    try {
      const hasFileName = !!fileName;
      const fileExt = hasFileName
        ? (fileName.split(".").pop() || "").toLowerCase()
        : this.extractFileExtension(url);

      const newFileName = hasFileName
        ? fileName.split(".").slice(0, -1).join(".")
        : "";

      const extWithDot = fileExt ? `.${fileExt}` : "";

      downloadFile(url, newFileName, extWithDot, needsAuth(url)).catch((error: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(error, this.$t("clientManagement.renewalRateCard.downloadFailed").toString())
        );
      });
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error as any, this.$t("clientManagement.renewalRateCard.downloadFailed").toString())
      );
    }
  }

  async downloadAttachmentBackground(): Promise<void> {
    const clientId = Number(this.detailData.id || 0);
    try {
      MainAppController.showLoading();
      await ClientManagementController.downloadAttachment(clientId);

      MainAppController.setSnackbarMessage(this.$t("clientManagement.renewalRateCard.downloadAttachmentEmail").toString());
      MainAppController.setSnackbarTimeout(3000);
      MainAppController.setSnackbar(true);
    } catch (err) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(err, this.$t("clientManagement.renewalRateCard.downloadAttachmentFailed").toString())
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  buildClientRateErrorFileName(
    type: string,
    clientCode: string | undefined | null
  ): string {
    return `Download File Gagal - ${type} - ${clientCode}${CLIENT_RATE_ERROR_FILE_EXT}`;
  }

  resolveClientPriceTypeLabel(priceType: string | undefined | null): string {
    const label: Record<string, string> = {
      normal: this.$t("clientManagement.renewalRateCard.priceTypeNormal").toString(),
      normal_custom_3lc: this.$t("clientManagement.renewalRateCard.priceTypeNormal").toString(),
      normal_retail_district: this.$t("clientManagement.renewalRateCard.priceTypeNormal").toString(),
      normal_custom_3lc_retail_district: this.$t("clientManagement.renewalRateCard.priceTypeNormal").toString(),
      custom_client: this.$t("clientManagement.renewalRateCard.priceTypeCustom").toString(),
      custom_client_retail_district: this.$t("clientManagement.renewalRateCard.priceTypeCustom").toString(),
      combination: this.$t("clientManagement.renewalRateCard.priceTypeCombination").toString(),
      combination_custom_3lc: this.$t("clientManagement.renewalRateCard.priceTypeCombination").toString(),
      combination_retail_district: this.$t("clientManagement.renewalRateCard.priceTypeCombination").toString(),
      combination_custom_3lc_retail_district: this.$t("clientManagement.renewalRateCard.priceTypeCombination").toString(),
    };
    if (priceType) {
      if (label[priceType]) {
        return label[priceType];
      } else {
        return priceType;
      }
    } else {
      return "Unknown";
    }
  }

  downloadErrorFile(): void {
    const renewalRateData =
      ClientManagementController.detailClientManagement.clientRenewalRate;

    const clientCode =
      ClientManagementController.detailClientManagement.clientCode;
    if (
      renewalRateData.clientRatePriceType === "combination" ||
      renewalRateData.clientRatePriceType === "custom_client"
    ) {
      const filename = this.buildClientRateErrorFileName(
        this.resolveClientPriceTypeLabel(renewalRateData.clientRatePriceType),
        clientCode
      );
      const fileId = renewalRateData.clientErrorFileCustomRate.split("/").pop();

      ClientController.getBulkErrorReport({
        id: Number(fileId),
        filename: filename
      });
    } else {
      this.downloadErrorFileBE(renewalRateData);
    }
  }

  downloadErrorFileBE(renewalRateData: any): void {
    try {
      const url = renewalRateData.clientErrorFileCustomRate;
      const fileName = renewalRateData.clientErrorNameCustomRate;

      const cleanUrl = (url || "").split("?")[0];
      const cleanFileName = (fileName || "").split("?")[0];

      // Try to read filename from query param ?path=...
      const queryPathName = this.extractFileNameFromPathParam(url);

      // Determine file extension: prefer from cleanFileName if it has a dot, otherwise from query path, fallback to URL
      const fileExt = (cleanFileName && cleanFileName.includes("."))
        ? (cleanFileName.split(".").pop() || "").toLowerCase()
        : (queryPathName && queryPathName.includes("."))
          ? (queryPathName.split(".").pop() || "").toLowerCase()
          : this.extractFileExtension(url);

      // Build base name source: prefer provided cleanFileName when it contains a dot, otherwise use query path name, fallback to URL basename
      const baseNameSource = (cleanFileName && cleanFileName.includes("."))
        ? cleanFileName
        : (queryPathName || fileNameFromUrl(cleanUrl));

      // Build new file name without extension (only strip if a dot exists)
      const newFileName = baseNameSource.includes(".")
        ? baseNameSource.split(".").slice(0, -1).join(".")
        : baseNameSource;
      const extWithDot = fileExt ? `.${fileExt}` : "";

      downloadFile(url, newFileName, extWithDot, needsAuth(url)).catch((error: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(error, this.$t("clientManagement.renewalRateCard.downloadFailed").toString())
        );
      });
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error as any, this.$t("clientManagement.renewalRateCard.downloadFailed").toString())
      );
    }
  }

  // Fallback: extract file extension from URL
  extractFileExtension(url: string): string {
    try {
      const [baseUrl, queryString] = (url || "").split("?");
      if (queryString) {
        const params = new URLSearchParams(queryString);
        const path = params.get("path");
        if (path) {
          const ext = path.split(".").pop() || "";
          return ext.toLowerCase();
        }
      }
      const ext = (baseUrl || url).split(".").pop() || "";
      return ext.toLowerCase();
    } catch {
      const ext = (url || "").split(".").pop() || "";
      return ext.toLowerCase();
    }
  }

  // Extract base file name (with extension) from "path" query param if present
  extractFileNameFromPathParam(url: string): string {
    try {
      if (!url) return "";
      const [, queryString] = url.split("?");
      if (!queryString) return "";
      const params = new URLSearchParams(queryString);
      const path = params.get("path") || "";
      const base = path.split("/").pop() || path;
      return base;
    } catch {
      return "";
    }
  }

  formateRatePeriod(start: string, end: string) {
    const isSameMonthYear =
      formatDateNormal(start, "MMMM-YYYY") ===
      formatDateNormal(end, "MMMM-YYYY");

    return `${formatDateNormal(
      start,
      isSameMonthYear ? "DD" : "DD MMMM YYYY"
    )} - ${formatDateNormal(end, "DD MMMM YYYY")}`;
  }

  get insuranceRateCalculation() {
    const insuranceValuePercentage = this.detailData.clientRenewalRate.clientRateInsuranceValuePercentage
      .toString()
      .replace(".", ",");
    switch (
      this.detailData.clientRenewalRate.clientRateInsuranceType.toLowerCase()
    ) {
      case "":
        return this.$t("clientManagement.renewalRateCard.retailTiering").toString();
      case "bundling":
        return this.$t("clientManagement.renewalRateCard.bundlingNoInsuranceFee").toString();
      case "custom":
        return `${this.$t("clientManagement.renewalRateCard.customRates")} ${insuranceValuePercentage}%`;
      default:
        return "-";
    }
  }

  get ableToRenewRatesByFlags() {
    return FlagsPermissionClient.permission_client_management_renew_rate.isEnabled();
  }

  get ableToUpdateLiveRateByFlags() {
    return FlagsPermissionClient.permission_client_management_update_live_rate.isEnabled();
  }

  changePeriodePrice = false;
  openChangePeriodePrice() {
    this.changePeriodePrice = !this.changePeriodePrice
  }

  get showButtonChangeInsurance() {
    const allowedStatuses = [
      "active",
      "pending",
      "requested",
      "nearly_expired"
    ];
    return allowedStatuses.includes(
      this.detailData.clientCurrentRate.clientStatusCustomRate
    );
  }
  changeInsurance() {
    this.isUpdateInsurance = true;
  }
  get decideDisableChangeInsuranceButton() {
    return (
      this.detailData.clientLiveRate.clientStatusCustomRate.toLowerCase() ===
        "uploading" || "requested"
    );
  }
  get showButtonChangeRate() {
    const allowedRacePriceType = [
      "custom_client",
      "custom_client_retail_district",
      "combination",
      "combination_custom_3lc",
      "combination_retail_district",
      "combination_custom_3lc_retail_district"
    ];
    const allowedStatuses = ["active", "nearly_expired"];
    return (
      allowedStatuses.includes(
        this.detailData.clientCurrentRate.clientStatusCustomRate
      ) &&
      allowedRacePriceType.includes(
        this.detailData.clientCurrentRate.clientRatePriceType
      )
    );
  }
  get decideDisableChangeRateButton() {
    return (
      this.detailData.clientLiveRate.clientStatusCustomRate.toLowerCase()  ===
        "uploading" || "requested"
    );
  }
  closeChangeInsurance() {
    this.isUpdateInsurance = false;
  }
  getDetailClientManagementV2() {
    ClientManagementController.getDetailV2(this.detailData.id);
  }

  get ratesRetryUploadConfig() {
    return FlagsPermissionClient.rates_retry_upload_enabled.isEnabled()
  }

  onOpenRetryConfirmationUploadRate() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: this.$t("clientManagement.renewalRateCard.retryProcessDataTitle").toString(),
        message: this.$t("clientManagement.renewalRateCard.retryProcessDataMessage").toString(),
        onSubmit: this.retryUploadClientRate,
        onClose: MainAppController.closeMessageModal,
        textSuccess: this.$t("clientManagement.renewalRateCard.yesContinue").toString(),
        textCancel: this.$t("clientManagement.renewalRateCard.cancel").toString(),
        customWidth: "417px",
        customButton: "w-full",
        customParentButton: "w-full",
        customWrapperButton: "w-full px-8",
      })
    );
  }

  get rateVersionUpdatedAt() {
    return formatDate(this.detailData.clientRenewalRate?.clientRateUpdatedAt);
  }

  get validationRetryButton() {
    if (!this.detailData.clientRenewalRate?.clientRateUpdatedAt) {
      return false
    }
    const now = new Date();
    const versionUpdateAt = new Date(this.detailData.clientRenewalRate?.clientRateUpdatedAt);
    const versionUpdateAtPlusOneHour = new Date(versionUpdateAt.getTime() + 60 * 60 * 1000);
    return now.getTime() < versionUpdateAtPlusOneHour.getTime();
  }

  async retryUploadClientRate() {
    MainAppController.closeMessageModal();
    const resp = await ClientController.retryUploadClientRate({
      clientId: this.detailData.id,
      rateVersionId: this.detailData.clientRenewalRate?.clientCustomRateId || 0
    });
    if (resp) {
      this.getDetailClientManagementV2()
    }
  }

  get isCancelUploadRate() {
    return (
      FlagsPermissionClient.abort_upload_rate_enabled.isEnabled() &&
      this.isUploading &&
      this.renewalCustomRate?.isStuck
    );
  }

  onConfirmCancelProcessUploadRate() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: this.$t("clientManagement.renewalRateCard.cancelProcessMessage").toString(),
        message: this.$t("clientManagement.renewalRateCard.cancelProcessMessageConfirm").toString(),
        onSubmit: this.onCancelProcessUploadRate,
        onClose: MainAppController.closeMessageModal,
        textSuccess: this.$t("clientManagement.renewalRateCard.yesCancel").toString(),
        textCancel: this.$t("clientManagement.renewalRateCard.back").toString(),
        customButton: "w-full",
        customParentButton: "w-full",
        customWrapperButton: "w-full px-8",
      })
    );
  }

  async onCancelProcessUploadRate() {
    try {
      MainAppController.closeMessageModal();
      MainAppController.showLoading();
      await ClientRegistrationController.abortUploadRate(
        new AbortUploadRateApiRequest({
          clientCustomRateId: this.isLiveRate
            ? this.detailData.clientLiveRate.clientCustomRateId
            : this.isRenewalRate
            ? this.detailData.clientRenewalRate.clientCustomRateId
            : this.detailData.clientCurrentRate.clientCustomRateId
        })
      );
      this.$emit("cancel-upload-rate");
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error, this.$t("clientManagement.renewalRateCard.cancelProcessFailed").toString())
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  get disableUbahPeriode() {
    return !FlagsPermissionClient.permission_period_date_time.isEnabled();
  }
}

export default RenewalCardCard;
